import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Target, TrendingUp, DollarSign, BarChart3, Loader2 } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { getPresets, setPreset } from '../utils/api';
import { useAppStore } from '../store';
import { useTranslation } from '../utils/translations';

export default function StrategiesPage() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { activeTab, setActiveStrategy } = useAppStore();
  const [presets, setPresets] = useState([]);
  const [currentPreset, setCurrentPreset] = useState('ICT_CORE');
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [changingPresetId, setChangingPresetId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Get user ID from Clerk
  const userId = user?.id || null;

  useEffect(() => {
    fetchPresets();
  }, []);

  // Ricarica i dati quando torni sulla tab strategies
  useEffect(() => {
    if (activeTab === 'strategies') {
      fetchPresets();
    }
  }, [activeTab]);

  const fetchPresets = async (skipLoading = false) => {
    try {
      if (!skipLoading) {
        setLoading(true);
      }
      setError('');
      const response = await getPresets(userId);
      const data = response.data;
      console.log('Fetched presets data:', data);
      console.log('📋 All presets:', data.presets?.map(p => ({ id: p.id, name: p.name, is_active: p.is_active })));
      
      setPresets(data.presets || []);
      const presetId = data.current_preset || 'ICT_CORE';
      console.log('📋 Current preset from backend:', presetId);
      console.log('📋 Preset type:', typeof presetId);
      console.log('📋 Preset normalized:', presetId?.toUpperCase()?.trim());
      
      // Normalize preset ID to ensure consistency (uppercase, trim)
      const normalizedPresetId = presetId ? String(presetId).toUpperCase().trim() : 'ICT_CORE';
      console.log('📋 Setting current preset to (normalized):', normalizedPresetId);
      setCurrentPreset(normalizedPresetId);
      
      // ✅ Aggiorna lo store globale con la strategia attiva
      setActiveStrategy(normalizedPresetId);
      console.log('📋 Initialized global store with activeStrategy:', normalizedPresetId);
    } catch (error) {
      console.error('Error fetching presets:', error);
      setError(t('strategies_load_error'));
    } finally {
      if (!skipLoading) {
        setLoading(false);
      }
    }
  };

  const selectPreset = async (presetId) => {
    if (currentPreset === presetId || changing) {
      console.log('Preset already active or changing:', { currentPreset, presetId, changing });
      return;
    }

    try {
      setChanging(true);
      setChangingPresetId(presetId);
      setError('');
      setSuccessMessage('');
      
      // Normalize preset ID to ensure consistency
      const normalizedPresetId = presetId ? String(presetId).toUpperCase().trim() : presetId;
      console.log('📋 Changing preset to (original):', presetId);
      console.log('📋 Changing preset to (normalized):', normalizedPresetId);
      console.log('📋 For user:', userId);
      
      const response = await setPreset(normalizedPresetId, userId);
      const data = response.data;
      
      console.log('Response from backend:', data);
      
      if (data.success) {
        // Il backend restituisce il preset salvato nella risposta
        const newPresetId = data.preset || normalizedPresetId;
        // Normalize the preset ID from backend response
        const normalizedNewPresetId = newPresetId ? String(newPresetId).toUpperCase().trim() : newPresetId;
        console.log('📋 Preset changed successfully to (from backend):', newPresetId);
        console.log('📋 Preset normalized:', normalizedNewPresetId);
        
        // Aggiorna immediatamente lo stato locale con preset normalizzato
        setCurrentPreset(normalizedNewPresetId);
        
        // ✅ IMPORTANTE: Aggiorna lo store globale per AI Analysis e Checklist
        setActiveStrategy(normalizedNewPresetId);
        console.log('📋 Updated global store with activeStrategy:', normalizedNewPresetId);
        
        // Trova il preset per il messaggio di successo
        // Cerca con preset normalizzato, poi originale
        const preset = presets.find(p => {
          const presetIdNormalized = p.id ? String(p.id).toUpperCase().trim() : p.id;
          return presetIdNormalized === normalizedNewPresetId || presetIdNormalized === normalizedPresetId;
        }) || presets.find(p => p.id === newPresetId) || presets.find(p => p.id === presetId);
        if (preset) {
          const message = t('strategies_changed_to').replace('{name}', preset.name);
          console.log('Success message:', message);
          setSuccessMessage(message);
          // Clear success message after 2 seconds
          setTimeout(() => setSuccessMessage(''), 2000);
        } else {
          console.warn('Preset not found in list:', newPresetId, 'Available:', presets.map(p => p.id));
        }
        
        // Ricarica i dati per assicurarsi che lo stato sia sincronizzato
        // Aspetta un po' per dare tempo al backend di salvare
        setTimeout(async () => {
          await fetchPresets(true); // skipLoading per non mostrare il loader
        }, 500);

        // Notifica altri componenti che la strategia è cambiata (usa preset normalizzato)
        window.dispatchEvent(new CustomEvent('strategy-changed', { 
          detail: { preset: normalizedNewPresetId } 
        }));
        console.log('📋 Dispatched strategy-changed event with preset:', normalizedNewPresetId);
      } else {
        throw new Error(data.message || t('strategies_change_error'));
      }
    } catch (error) {
      console.error('Error setting preset:', error);
      setError(error.response?.data?.message || error.message || t('strategies_change_error'));
    } finally {
      setChanging(false);
      setChangingPresetId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative mb-5">
          <div className="absolute inset-0 bg-violet-500/20 rounded-full blur-xl animate-pulse"></div>
          <Loader2 className="w-10 h-10 text-violet-400 animate-spin mx-auto relative z-10" />
        </div>
        <p className="text-slate-300 font-medium">{t('strategies_loading')}</p>
        <div className="mt-4 w-full max-w-xs h-1 bg-slate-800/50 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 via-purple-500 to-violet-500 rounded-full loading-shimmer"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card p-6">
        <h1 className="text-3xl font-bold text-white mb-3 tracking-tight">{t('strategies_title')}</h1>
        <p className="text-slate-300 text-sm leading-relaxed">
          {t('strategies_description')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Success Message - Mostra solo per 2 secondi */}
      {successMessage && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 animate-fadeIn">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Strategy Cards */}
      <div className="grid grid-cols-1 gap-6">
              {presets.map((preset) => {
          // Normalize preset IDs for comparison
          const presetIdNormalized = preset.id ? String(preset.id).toUpperCase().trim() : preset.id;
          const currentPresetNormalized = currentPreset ? String(currentPreset).toUpperCase().trim() : currentPreset;
          const isActive = currentPresetNormalized === presetIdNormalized;
          return (
            <div
              key={preset.id}
              className={`glass-card p-6 transition-all ${
                isActive
                  ? 'border-2 border-violet-500/50 bg-violet-500/5 shadow-lg shadow-violet-500/10'
                  : 'border border-slate-700/50 hover:border-slate-600/50'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3.5">
                  <h2 className="text-2xl font-bold text-white tracking-tight">{preset.name}</h2>
                  {isActive ? (
                    <span className="flex items-center gap-1.5 px-3.5 py-1.5 bg-violet-500/20 text-violet-300 rounded-full text-xs font-bold border-2 border-violet-500/60 shadow-medium shadow-violet-500/30 animate-pulse">
                      <CheckCircle className="w-3.5 h-3.5" />
                      {t('strategies_active')}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Description */}
              <p className="text-slate-300 mb-6 text-sm leading-relaxed">
                {preset.description}
              </p>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="glass-panel rounded-xl p-4 hover-lift transition-smooth">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="p-1.5 bg-cyan-500/20 rounded-lg">
                      <TrendingUp className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">{t('strategies_frequency')}</span>
                  </div>
                  <div className="text-base font-bold text-white">{preset.frequency}</div>
                </div>

                <div className="glass-panel rounded-xl p-4 hover-lift transition-smooth">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">{t('strategies_rr_target')}</span>
                  </div>
                  <div className="text-base font-bold text-white">{preset.rr_target}</div>
                </div>

                <div className="glass-panel rounded-xl p-4 hover-lift transition-smooth">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="p-1.5 bg-violet-500/20 rounded-lg">
                      <Target className="w-4 h-4 text-violet-400" />
                    </div>
                    <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">{t('strategies_entry_threshold')}</span>
                  </div>
                  <div className="text-base font-bold text-white">{preset.entry_threshold}%</div>
                </div>

                <div className="glass-panel rounded-xl p-4 hover-lift transition-smooth">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="p-1.5 bg-amber-500/20 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-xs text-slate-300 uppercase tracking-wider font-semibold">{t('strategies_max_points')}</span>
                  </div>
                  <div className="text-base font-bold text-white">{preset.max_total}</div>
                </div>
              </div>

              {/* Special Requirements */}
              {(preset.requires_liquidity_sweep || preset.requires_all_core) && (
                <div className="mb-6 space-y-2">
                  {preset.requires_liquidity_sweep && (
                    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-amber-400">
                        {t('strategies_requires_liquidity_sweep')}
                      </span>
                    </div>
                  )}
                  {preset.requires_all_core && (
                    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-amber-400">
                        {t('strategies_requires_all_core')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={() => {
                  const presetIdToUse = preset.id ? String(preset.id).toUpperCase().trim() : preset.id;
                  console.log('📋 Clicked preset:', preset.id, '-> normalized:', presetIdToUse);
                  selectPreset(presetIdToUse);
                }}
                disabled={isActive || changing}
                className={`w-full py-3.5 px-5 rounded-xl font-bold text-sm transition-smooth active:scale-[0.98] focus-ring ${
                  isActive
                    ? 'bg-violet-500/20 text-violet-300 cursor-not-allowed border-2 border-violet-500/60 shadow-soft'
                    : changing && changingPresetId === preset.id
                    ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed'
                    : changing
                    ? 'bg-slate-800/30 text-slate-500 cursor-not-allowed opacity-50'
                    : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-medium shadow-violet-500/25 hover:shadow-strong hover:shadow-violet-500/30'
                }`}
                title={isActive ? t('strategies_already_active') : ''}
              >
                {changing && changingPresetId === preset.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('strategies_loading')}
                  </span>
                ) : isActive ? (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {t('strategies_active')}
                  </span>
                ) : (
                  t('strategies_use_this')
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Info Footer */}
      <div className="glass-card p-4 border border-slate-700/50">
        <p className="text-xs text-slate-400 text-center">
          {t('strategies_footer_info')}
        </p>
      </div>
    </div>
  );
}

